import { customElement, property, query } from 'lit/decorators.js';
import { html, LitElement, nothing } from "lit";
import { TimelineData } from '../types';
import { LoadMoreOnScrollController } from '../../reactiveControllers/LoadMoreOnScrollController';

@customElement('gz-load-more-timeline')
export class GzCollapsibleTimelineLoadMore extends LitElement {

  @property({ type: Array })
  data: TimelineData = [];

  @property({ type: Boolean })
  isLoading = false;

  @property({ type: Boolean })
  hasMoreItems = false;

  loadMoreItems?: () => Promise<TimelineData>;

  @query('#sentinel')
  private sentinel!: HTMLDivElement;

  // Instantiate the controller that will handle the infinite scroll
  private readonly loadMoreController = new LoadMoreOnScrollController(this, {
    onLoadMore: () => this.onLoadMore(),
    // IntersectionObserver options
    observerOptions: {
      root: null, // or this if the component is scrollable
      threshold: 0.1,
    },
    getSentinelElement: () => this.sentinel,
  });

  private async onLoadMore(): Promise<void> {
    // If we're already loading, skip
    if (this.isLoading) {
      return undefined;
    }

    if (!this.loadMoreItems) {
      return;
    }

    this.isLoading = true;
    const newItems: TimelineData = await this.loadMoreItems();

    if (newItems.length === 0) {
      // No more items => remove sentinel, which will stop the controller from firing
      this.hasMoreItems = false;
    } else {
      this.data = [...this.data, ...newItems] as TimelineData;
    }

    // Done fetching
    this.isLoading = false;
  }

  render() {
    return html`
      <div style="display: block; width: 100%; height: 200px; overflow-y: scroll">
        <gz-collapsible-timeline .data="${this.data}" style="max-height: fit-content"></gz-collapsible-timeline>
        ${this.hasMoreItems
      ? html`
              <div id="sentinel" class="scroll-guard">
                <!-- Show spinner if loading -->
                ${this.isLoading
        ? html`<gz-progress-spinner indeterminate spinnerWidth="medium"></gz-progress-spinner>`
        : nothing}
              </div>
            `
      : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-load-more-timeline': GzCollapsibleTimelineLoadMore;
  }
}
